import { useCallback } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Slider,
} from '@design-system';
import { formatCurrency } from '@shared/formatCurrency';

import { usePremiumAmountInput } from '@/features/guided-financial-planning/hooks';
import { snapPremiumToStep } from '@/features/guided-financial-planning/utils/premium-bounds-from-quotes';

interface SetPremiumDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: number;
  min: number;
  max: number;
  step: number;
  onValueChange: (value: number) => void;
  onApply: (appliedPremium: number) => void;
  isApplying?: boolean;
}

export function SetPremiumDialog({
  open,
  onOpenChange,
  value,
  min,
  max,
  step,
  onValueChange,
  onApply,
  isApplying = false,
}: SetPremiumDialogProps) {
  const clampedValue = Math.max(min, Math.min(max, value));
  const sliderValue = snapPremiumToStep(clampedValue, min, max, step);

  const {
    commitDraft,
    handlePremiumInputChange,
    handlePremiumInputFocus,
    inputDisplayValue,
    clearAmountDraft,
  } = usePremiumAmountInput({
    clampedValue,
    min,
    max,
    step,
    onPersist: onValueChange,
    resetDraftWhenOpen: open,
  });

  const handleApplyClick = useCallback(() => {
    const next = commitDraft();
    onApply(next);
  }, [commitDraft, onApply]);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Set Premium</DialogTitle>
          <DialogDescription>
            Use the slider or type an amount you&apos;re comfortable investing in coverage.
            We&apos;ll model quotes at that premium.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col pt-2">
          <div className="mx-auto w-full max-w-sm space-y-2 text-center">
            <Label
              htmlFor="set-premium-dialog-amount"
              className="text-muted-foreground text-sm font-normal"
            >
              Target premium
            </Label>
            <div className="relative">
              <span
                className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xl font-medium"
                aria-hidden
              >
                $
              </span>
              <Input
                id="set-premium-dialog-amount"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                disabled={isApplying}
                value={inputDisplayValue}
                onChange={handlePremiumInputChange}
                onFocus={handlePremiumInputFocus}
                onBlur={() => {
                  commitDraft();
                }}
                className="text-primary min-h-14 w-full py-2.5 pl-10 text-center text-2xl font-semibold leading-tight tracking-tight md:text-2xl"
                aria-describedby="set-premium-dialog-range-hint"
              />
            </div>
            <div
              id="set-premium-dialog-range-hint"
              className="text-muted-foreground flex w-full justify-between text-sm"
            >
              <span className="text-left">Min {formatCurrency(min)}</span>
              <span className="text-right">Max {formatCurrency(max)}</span>
            </div>
          </div>

          <div className="mt-8 space-y-4">
            <Slider
              value={[sliderValue]}
              onValueChange={nextValue => {
                clearAmountDraft();
                const raw = nextValue[0] ?? sliderValue;
                onValueChange(snapPremiumToStep(raw, min, max, step));
              }}
              min={min}
              max={max}
              step={step}
            />

            <div className="text-muted-foreground flex items-center justify-between text-sm">
              <span>{formatCurrency(min)}</span>
              <span>{formatCurrency(max)}</span>
            </div>
          </div>

          <div className="mt-5 flex items-center justify-end gap-2 pt-1">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={isApplying}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApplyClick}
              disabled={isApplying}
            >
              {isApplying ? 'Applying...' : 'Apply'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
